import React from 'react'

import { Box, Spinner, Text } from '@factorialco/deprecated-design-system'

import Selector from 'modules/core/components/Selector'
import SelectOrCreate from 'modules/core/components/SelectOrCreate'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { useCustomResourceFields } from 'modules/custom_resources/hooks/customResources/useCustomResourceFields'
import CustomFieldModel from 'modules/custom_resources/store/models/customFields/Field'
import { useFormProvider } from 'modules/processes/components/ItemWizard/Providers/form'
import { usePreviewProvider } from 'modules/processes/components/ItemWizard/Providers/preview'
import { useValidationsProvider } from 'modules/processes/components/ItemWizard/Providers/validations'
import { useTaskWizard } from 'modules/processes/components/ItemWizard/Tasks/TaskProvider'
import { DataCollectionCategories } from 'modules/processes/domain'

interface Iconfiguration {
  field_ids: string[]
}

interface ICategory {
  category: string
  configuration?: Iconfiguration
}

interface IOption {
  label: string
  value: string
}

const CONFIGURATION_FIELD = 'task_type_configuration'

/**
 * Compares a field ID (which may be a number from REST API or JSON.parse)
 * with a target string ID, normalizing both to strings first.
 *
 * After the BigInt migration (PR #85001), GraphQL ID scalars return strings,
 * but field IDs from REST responses and JSON blobs may still be numbers.
 * This ensures strict-equality comparisons work regardless of the source type.
 */
export const matchFieldId = (
  fieldId: string | number | null | undefined,
  targetId: string
): boolean => String(fieldId) === targetId

const FieldSelector = () => {
  const { setFieldValidation, removeFieldValidation } = useValidationsProvider()
  const { setTaskConfiguration } = useTaskWizard()
  const { setPreviewValues } = usePreviewProvider()
  const [selectedFieldIds, setselectedFieldIds] = React.useState<string[]>([])
  const { associatedTaskItem: item, form } = useFormProvider()
  const {
    loading: loadingCustomResourceFields,
    customResourceFields,
    controls,
  } = useCustomResourceFields({
    usageGroupSlugs: [CustomFieldModel.UsageGroups.contractVersions],
  })

  const currentTaskConfiguration = form.get('task_type_configuration')?.value

  const [selectedCategories, setSelectedCategories] = React.useState<string[]>([])

  const options = useMemo(() => {
    const data: IOption[] = [
      {
        label: i18n.t(
          'processes.process_settings.wizard.tasks.types.contract_data.category_selector.start_dates'
        ),
        value: DataCollectionCategories.start_date,
      },
      {
        label: i18n.t(
          'processes.process_settings.wizard.tasks.types.contract_data.category_selector.role'
        ),
        value: DataCollectionCategories.role,
      },
      {
        label: i18n.t(
          'processes.process_settings.wizard.tasks.types.contract_data.category_selector.salary'
        ),
        value: DataCollectionCategories.salary,
      },
      {
        label: i18n.t(
          'processes.process_settings.wizard.tasks.types.contract_data.category_selector.contract_hours'
        ),
        value: DataCollectionCategories.contract_hours,
      },
    ]

    if (customResourceFields) {
      customResourceFields.forEach((resource) => {
        const { id, labelText } = resource?.field || {}
        if (!id || !labelText) return
        data.push({
          label:
            resource?.fieldableType === 'LegalEntity' && resource?.legalEntity
              ? `${labelText} (${resource?.legalEntity?.legalName})`
              : labelText,
          value: `${DataCollectionCategories.contract_custom_fields}-${id}`,
        })
      })
    }

    return data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customResourceFields?.length, loadingCustomResourceFields])

  const extractCategories = (taskTypeConfiguration?: string) => {
    if (!taskTypeConfiguration) return []

    const categories: string[] = (
      JSON.parse(taskTypeConfiguration).configuration_fields?.at(0)?.categories || []
    ).flatMap((category: ICategory) => {
      const { category: categoryValue, configuration } = category
      if (categoryValue === DataCollectionCategories.contract_custom_fields) {
        return configuration?.field_ids.map(
          (id: string) => `${DataCollectionCategories.contract_custom_fields}-${id}`
        )
      }
      return [categoryValue]
    })
    return categories
  }

  useEffect(() => {
    setFieldValidation({
      field: CONFIGURATION_FIELD,
      validation: () => !!selectedCategories.length,
    })

    return () => {
      removeFieldValidation(CONFIGURATION_FIELD)
    }
  }, [selectedCategories.join(',')]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const currentCategories = currentTaskConfiguration?.configuration_fields
      ? extractCategories(JSON.stringify(currentTaskConfiguration?.configuration_fields))
      : []
    const hasCategorySelected = currentCategories.length > 0

    if (item && item.taskTypeConfiguration && !hasCategorySelected) {
      const categories = extractCategories(item.taskTypeConfiguration)
      setSelectedCategories(categories)
    } else if (hasCategorySelected && currentCategories) {
      setSelectedCategories(currentCategories)
    } else {
      setSelectedCategories([
        DataCollectionCategories.start_date,
        DataCollectionCategories.role,
        DataCollectionCategories.salary,
        DataCollectionCategories.contract_hours,
      ])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onCategoriesChange = (values: string[]) => {
    const fieldIds = values
      .filter((category) => category.includes('-'))
      .map((category) => String(category.split('-')[1]))
    setselectedFieldIds(fieldIds)
    setSelectedCategories(values)
  }

  useEffect(() => {
    setTaskConfiguration({
      configuration_fields: [
        {
          resource: 'contract',
          categories: selectedCategories.reduce((memo: ICategory[], category) => {
            if (category.includes('-')) {
              if (memo.some((c) => c.category === DataCollectionCategories.contract_custom_fields))
                return memo

              memo.push({
                category: DataCollectionCategories.contract_custom_fields,
                configuration: {
                  field_ids: selectedFieldIds,
                },
              })
              return memo
            }

            memo.push({
              category,
            })

            return memo
          }, []),
        },
      ],
    })

    setPreviewValues({
      selectedFields: selectedFieldIds.map(
        (id) =>
          customResourceFields?.find((resource) => matchFieldId(resource?.field?.id, id))?.field
      ),
      selectedCategories,
    })
  }, [selectedCategories.join(','), selectedFieldIds.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const categoriesOptions = options.map((category) => {
    return (
      <Selector.Option
        key={category.value}
        value={category.value}
        label={category.label}
        searchValue={category.label}
      >
        <Box flexDirection="row" gap="s4" width="full" justifyContent="spaceBetween">
          <Text.Normal ellipsis weight="medium">
            {category.label}
          </Text.Normal>
        </Box>
      </Selector.Option>
    )
  })

  if (loadingCustomResourceFields)
    return (
      <Box alignItems="center">
        <Spinner />
      </Box>
    )

  return (
    <>
      <SelectOrCreate.Popover
        label={i18n.t('processes.process_settings.wizard.tasks.types.contract_data.selector_field')}
        placeholder={i18n.t('common.select')}
        values={selectedCategories}
        onChange={onCategoriesChange}
        onValuesChange={onCategoriesChange}
        mandatory
        isMultiSelect
        hasMore={controls.hasMore}
        fetchMore={controls.fetchMore}
        loading={controls.loading}
        clearAllButton={selectedCategories.length > 0}
      >
        {categoriesOptions}
      </SelectOrCreate.Popover>
      {controls.hasMore && (
        <Box paddingTop="s4">
          <Text.Normal size="100" color="textSecondary">
            {i18n.t(
              'processes.process_settings.wizard.tasks.types.contract_data.category_selector.scroll_for_more_fields'
            )}
          </Text.Normal>
        </Box>
      )}
    </>
  )
}

export default FieldSelector
